import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  PictureAsPdf,
  InsertDriveFile,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MyReimbursements = () => {
  const [reimbursements, setReimbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchReimbursements = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = sessionStorage.getItem("token");

        const response = await fetch(
          `${BASE_URL}/api/reimbursement/logineduser/get-reimbursement-detail`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.msg || "Failed to fetch reimbursements");
        }

        const data = await response.json();
        setReimbursements(data);
      } catch (error) {
        console.error("Error fetching reimbursements:", error);
        setError(error.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchReimbursements();
  }, []);

  const handleExpandClick = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const getFileIcon = (fileUrl) => {
    const extension = fileUrl.split(".").pop().toLowerCase();
    if (["pdf"].includes(extension)) {
      return <PictureAsPdf fontSize="small" sx={{ color: "#EF4444" }} />;
    }
    return <InsertDriveFile fontSize="small" sx={{ color: "#8b8fa3" }} />;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "\u20B90.00";
    return `\u20B9${parseFloat(amount).toFixed(2)}`;
  };

  // Table styles
  const headerCellSx = {
    bgcolor: "#f5f7fb",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "#5a5f7a",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    py: 1.5,
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    fontSize: "0.875rem",
    color: "#1a1a2e",
    py: 2,
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="My Reimbursements"
        subtitle="View and track your expense claims"
        icon={<ReceiptLongIcon />}
        actions={
          <Button
            variant="contained"
            href="/submit-reimbursement"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
            }}
          >
            New Request
          </Button>
        }
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#667eea", mb: 2 }} />
          <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
            Loading your reimbursements...
          </Typography>
        </Box>
      ) : error ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #FCA5A5",
            borderRadius: "16px",
            p: 3,
            bgcolor: "#FEF2F2",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#991B1B", fontWeight: 500 }}>
            {error}
          </Typography>
        </Paper>
      ) : reimbursements.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: "1px solid #eef0f6", borderRadius: "16px" }}
        >
          <EmptyState
            title="No reimbursements found"
            subtitle="You haven't submitted any reimbursement requests yet."
            action={
              <Button
                variant="contained"
                href="/submit-reimbursement"
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 3,
                  "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
                }}
              >
                Submit New Reimbursement
              </Button>
            }
          />
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {reimbursements.map((reimbursement, index) => (
            <Paper
              key={reimbursement._id}
              elevation={0}
              sx={{
                border: "1px solid #eef0f6",
                borderRadius: "16px",
                overflow: "hidden",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: "#CBD5E1" },
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2.5,
                  cursor: "pointer",
                }}
                onClick={() => handleExpandClick(index)}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a2e" }}
                  >
                    {reimbursement.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#8b8fa3", mt: 0.25 }}>
                    {new Date(reimbursement.fromDate).toLocaleDateString()} -{" "}
                    {new Date(reimbursement.toDate).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e" }}
                  >
                    {formatCurrency(reimbursement.totalAmount)}
                  </Typography>
                  <IconButton size="small" sx={{ color: "#8b8fa3" }}>
                    {expanded[index] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              </Box>

              {/* Expanded Detail */}
              <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2.5, pb: 2.5 }}>
                  {/* Meta row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      mb: 2,
                      pb: 2,
                      borderTop: "1px solid #eef0f6",
                      borderBottom: "2px solid #f0f0f5",
                      pt: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {[
                      { label: "Manager", value: reimbursement.managerName },
                      { label: "Department", value: reimbursement.department },
                      { label: "Purpose", value: reimbursement.purpose },
                    ].map((item) => (
                      <Box key={item.label}>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#8b8fa3",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontWeight: 600,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 500 }}
                        >
                          {item.value || "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Expense Table */}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: "1px solid #eef0f6",
                      borderRadius: "10px",
                      overflowX: "auto",
                      maxWidth: "100%",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Date</TableCell>
                          <TableCell sx={headerCellSx}>Description</TableCell>
                          <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
                            Amount
                          </TableCell>
                          <TableCell sx={headerCellSx}>Status</TableCell>
                          <TableCell sx={headerCellSx}>Files</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reimbursement.expenses.map((expense) => (
                          <TableRow key={expense._id} hover>
                            <TableCell sx={bodyCellSx}>
                              {new Date(expense.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {expense.description}
                              </Typography>
                              {expense.note && (
                                <Typography
                                  sx={{ fontSize: "0.75rem", color: "#8b8fa3", mt: 0.25 }}
                                >
                                  Note: {expense.note}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...bodyCellSx,
                                textAlign: "right",
                                fontWeight: 600,
                              }}
                            >
                              {formatCurrency(expense.total)}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <StatusChip
                                status={expense.status || "pending"}
                              />
                              {expense.remark && (
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "#8b8fa3",
                                    mt: 0.5,
                                    fontStyle: "italic",
                                  }}
                                >
                                  {expense.remark}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {expense.files.map((file, fileIndex) => (
                                  <Box
                                    key={fileIndex}
                                    component="a"
                                    href={file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      textDecoration: "none",
                                      color: "#667eea",
                                      fontSize: "0.8rem",
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                  >
                                    {getFileIcon(file)}
                                    <span>File {fileIndex + 1}</span>
                                  </Box>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}
    </PageContainer>
  );
};

export default MyReimbursements;
